/**
 * three.js force/torque overlay layer (pattern: pointLayers.ts).
 *
 * Draws, per solved BiomechFrame: joint reaction force arrows (length scaled by body weight),
 * torque rings coloured green→amber→red by joint load, contact force arrows and an optional
 * residual arrow at the whole-body COM. Geometries and materials are created once and reused;
 * a frame update only touches transforms, so there is no per-frame allocation.
 */
import * as THREE from "three";
import {
  DEFAULT_OVERLAY_OPTIONS,
  jointDisplayName,
  resolveOverlayText,
  type BiomechFrame,
  type OverlayOptions,
  type Vec3,
} from "./types.ts";

const UP = new THREE.Vector3(0, 1, 0);
const LABEL_MIN_INTERVAL_MS = 250;

/** Load → colour buckets (green → amber → red). */
export function loadColorIndex(load: number): 0 | 1 | 2 {
  if (load >= 1) return 2;
  if (load >= 0.6) return 1;
  return 0;
}

const LOAD_COLORS = [0x36c46a, 0xf2b53b, 0xe8453c];

interface Pool<T extends THREE.Object3D> {
  items: T[];
  used: number;
}

function makePool<T extends THREE.Object3D>(): Pool<T> {
  return { items: [], used: 0 };
}

export interface PersonScale {
  /** Body weight in newtons, used to normalise arrow lengths. */
  bodyWeightN: number;
}

export interface ForceOverlay3DOptions extends OverlayOptions {
  /** Metres of arrow per body weight of force (default 0.55). */
  metresPerBodyWeight?: number;
}

export class ForceOverlay3D {
  private readonly root = new THREE.Group();
  private readonly arrowGeo: THREE.CylinderGeometry;
  private readonly headGeo: THREE.ConeGeometry;
  private readonly ringGeo: THREE.TorusGeometry;
  private readonly sphereGeo: THREE.SphereGeometry;
  private readonly forceMat: THREE.MeshBasicMaterial;
  private readonly groundMat: THREE.MeshBasicMaterial;
  private readonly personMat: THREE.MeshBasicMaterial;
  private readonly residualMat: THREE.MeshBasicMaterial;
  private readonly loadMats: THREE.MeshBasicMaterial[];

  private readonly arrows = makePool<THREE.Group>();
  private readonly rings = makePool<THREE.Mesh>();
  private readonly labels = makePool<THREE.Sprite>();
  private readonly labelText = new WeakMap<THREE.Sprite, string>();

  private options: ForceOverlay3DOptions = { ...DEFAULT_OVERLAY_OPTIONS };
  private text = resolveOverlayText(null);
  private metresPerBw = 0.55;
  private lastLabelDraw = 0;
  private disposed = false;

  constructor(parent: THREE.Object3D) {
    this.root.name = "biomechForceOverlay";
    this.root.visible = false;
    parent.add(this.root);

    // Unit parts spanning 0..1 along +Y so only scale.y changes per frame.
    this.arrowGeo = new THREE.CylinderGeometry(0.011, 0.011, 1, 8, 1, true);
    this.arrowGeo.translate(0, 0.5, 0);
    this.headGeo = new THREE.ConeGeometry(0.032, 1, 10);
    this.headGeo.translate(0, 0.5, 0);
    this.ringGeo = new THREE.TorusGeometry(1, 0.12, 8, 24);
    this.sphereGeo = new THREE.SphereGeometry(1, 10, 8);

    const mat = (color: number, opacity = 0.9) =>
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest: false,
        depthWrite: false,
      });
    this.forceMat = mat(0x4da3ff);
    this.groundMat = mat(0x8ce27a);
    this.personMat = mat(0xc084fc);
    this.residualMat = mat(0xff5d5d, 0.8);
    this.loadMats = LOAD_COLORS.map((c) => mat(c, 0.75));
  }

  get object(): THREE.Object3D {
    return this.root;
  }

  setVisible(visible: boolean): void {
    this.root.visible = !!visible;
  }

  setOptions(options: Partial<ForceOverlay3DOptions>): void {
    this.options = { ...this.options, ...options };
    if (options.text !== undefined) this.text = resolveOverlayText(options.text);
    if (options.metresPerBodyWeight && options.metresPerBodyWeight > 0) {
      this.metresPerBw = options.metresPerBodyWeight;
    }
  }

  /** Clear everything drawn (keeps pooled objects for reuse). */
  clear(): void {
    this.recycle();
  }

  /**
   * Draw one solved frame. `bodyWeights` maps person id → body weight in newtons
   * (falls back to 70 kg when a person is missing).
   */
  update(frame: BiomechFrame | null, bodyWeights: Map<number, number>): void {
    if (this.disposed) return;
    this.recycle();
    if (!frame) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const drawLabels = this.options.labels && now - this.lastLabelDraw >= LABEL_MIN_INTERVAL_MS;
    if (drawLabels) this.lastLabelDraw = now;

    for (const person of frame.persons) {
      const bw = Math.max(1, bodyWeights.get(person.id) ?? 70 * 9.81);
      for (const [jointId, joint] of Object.entries(person.joints)) {
        if (!joint) continue;
        if (this.options.forces) {
          this.arrow(joint.p, joint.f, bw, this.forceMat);
          if (this.options.labels) {
            this.label(
              joint.p,
              `${jointDisplayName(this.text, jointId)} ${Math.round(magnitude(joint.f))} N`,
              drawLabels,
            );
          }
        }
        if (this.options.torques) {
          this.torque(joint.p, joint.m, joint.load);
        }
      }
      if (this.options.residual) {
        this.arrow(person.com, person.residual.f, bw, this.residualMat);
      }
    }

    if (this.options.contacts) {
      for (const contact of frame.contacts) {
        const bw = Math.max(1, bodyWeights.get(contact.personId) ?? 70 * 9.81);
        this.arrow(
          contact.p,
          contact.f,
          bw,
          contact.kind === "ground" ? this.groundMat : this.personMat,
        );
      }
    }

    this.hideUnused();
  }

  /* -------------------------------------------------------------- draw */

  private arrow(at: Vec3, vector: Vec3, bodyWeightN: number, material: THREE.Material): void {
    const mag = magnitude(vector);
    if (mag < bodyWeightN * 0.02) return;
    const group = this.acquireArrow();
    const length = clamp((mag / bodyWeightN) * this.metresPerBw, 0.06, 2.5);
    group.position.set(at[0], at[1], at[2]);
    tmpDir.set(vector[0], vector[1], vector[2]).normalize();
    group.quaternion.setFromUnitVectors(UP, tmpDir);
    const headLength = Math.min(0.16, length * 0.4);
    const shaft = group.children[0] as THREE.Mesh;
    const head = group.children[1] as THREE.Mesh;
    shaft.material = material;
    head.material = material;
    shaft.scale.set(1, Math.max(0.001, length - headLength), 1);
    head.position.y = length - headLength;
    head.scale.set(1, headLength, 1);
    group.visible = true;
  }

  private torque(at: Vec3, moment: Vec3, load: number): void {
    const mag = magnitude(moment);
    if (mag < 1) return;
    const ring = this.acquireRing();
    const radius = clamp(0.05 + 0.09 * Math.min(load, 1.5), 0.05, 0.22);
    ring.position.set(at[0], at[1], at[2]);
    tmpDir.set(moment[0], moment[1], moment[2]).normalize();
    // Torus lies in its local XY plane; its axis is +Z.
    ring.quaternion.setFromUnitVectors(FORWARD, tmpDir);
    ring.scale.setScalar(radius);
    ring.material = this.loadMats[loadColorIndex(load)]!;
    ring.visible = true;
  }

  private label(at: Vec3, text: string, redraw: boolean): void {
    const sprite = this.acquireLabel();
    sprite.position.set(at[0], at[1] + 0.08, at[2]);
    sprite.visible = true;
    if (this.labelText.get(sprite) === text) return;
    if (!redraw) return;
    drawLabelTexture(sprite, text);
    this.labelText.set(sprite, text);
  }

  /* -------------------------------------------------------------- pool */

  private acquireArrow(): THREE.Group {
    const pool = this.arrows;
    if (pool.used < pool.items.length) return pool.items[pool.used++]!;
    const group = new THREE.Group();
    const shaft = new THREE.Mesh(this.arrowGeo, this.forceMat);
    const head = new THREE.Mesh(this.headGeo, this.forceMat);
    shaft.frustumCulled = false;
    head.frustumCulled = false;
    shaft.renderOrder = 10;
    head.renderOrder = 10;
    group.add(shaft, head);
    this.root.add(group);
    pool.items.push(group);
    pool.used++;
    return group;
  }

  private acquireRing(): THREE.Mesh {
    const pool = this.rings;
    if (pool.used < pool.items.length) return pool.items[pool.used++]!;
    const mesh = new THREE.Mesh(this.ringGeo, this.loadMats[0]!);
    mesh.frustumCulled = false;
    mesh.renderOrder = 11;
    this.root.add(mesh);
    pool.items.push(mesh);
    pool.used++;
    return mesh;
  }

  private acquireLabel(): THREE.Sprite {
    const pool = this.labels;
    if (pool.used < pool.items.length) return pool.items[pool.used++]!;
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 64;
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.34, 0.085, 1);
    sprite.renderOrder = 12;
    (sprite as THREE.Sprite & { userData: { canvas: HTMLCanvasElement } }).userData.canvas = canvas;
    this.root.add(sprite);
    pool.items.push(sprite);
    pool.used++;
    return sprite;
  }

  private recycle(): void {
    this.arrows.used = 0;
    this.rings.used = 0;
    this.labels.used = 0;
  }

  private hideUnused(): void {
    hideFrom(this.arrows);
    hideFrom(this.rings);
    hideFrom(this.labels);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const sprite of this.labels.items) {
      const mat = sprite.material as THREE.SpriteMaterial;
      mat.map?.dispose();
      mat.dispose();
    }
    this.arrowGeo.dispose();
    this.headGeo.dispose();
    this.ringGeo.dispose();
    this.sphereGeo.dispose();
    this.forceMat.dispose();
    this.groundMat.dispose();
    this.personMat.dispose();
    this.residualMat.dispose();
    for (const m of this.loadMats) m.dispose();
    this.root.clear();
    this.root.parent?.remove(this.root);
    this.arrows.items.length = 0;
    this.rings.items.length = 0;
    this.labels.items.length = 0;
  }
}

const tmpDir = new THREE.Vector3();
const FORWARD = new THREE.Vector3(0, 0, 1);

function hideFrom<T extends THREE.Object3D>(pool: Pool<T>): void {
  for (let i = pool.used; i < pool.items.length; i++) {
    pool.items[i]!.visible = false;
  }
}

function magnitude(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function drawLabelTexture(sprite: THREE.Sprite, text: string): void {
  const canvas = (sprite.userData as { canvas?: HTMLCanvasElement }).canvas;
  const ctx = canvas?.getContext("2d");
  if (!canvas || !ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(12,16,24,0.72)";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#e8f1ff";
  ctx.font = "600 30px system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 10, canvas.height / 2);
  const mat = sprite.material as THREE.SpriteMaterial;
  if (mat.map) mat.map.needsUpdate = true;
}
